import React, { useState, useEffect, useCallback } from "react";
import { Row, Col, Card, Space, Button, Statistic, Select } from "antd";
import {
  PlusOutlined,
  SyncOutlined,
  FileTextOutlined,
  ClockCircleOutlined,
  CheckCircleOutlined,
  BookOutlined,
} from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import useInstructionalMaterials from "../../../hooks/academics/useInstructionalMaterials";
import useAcademicYears from "../../../hooks/common/useAcademicYears";
import useTerms from "../../../hooks/common/useTerms";
import MaterialsTable from "../../../components/academics/materials/MaterialsTable";
import MaterialFormModal from "../../../components/academics/materials/MaterialFormModal";
import MaterialDetailDrawer from "../../../components/academics/materials/MaterialDetailDrawer";
import ValuesStore from "../../../store/values-store";
import { appraisalRoles } from "../../academics/appraisal/appraisalUtils";
import Utilities from "../../../dependencies/custom/react-utilities";
import domainSettings from "../../../dependencies/custom/settings";

const MATERIAL_TYPE_OPTIONS = [
  { value: "scheme", label: "Scheme of Work" },
  { value: "lesson_note", label: "Lesson Note" },
  { value: "lesson_plan", label: "Lesson Plan" },
  { value: "teaching_aid", label: "Teaching Aid" },
  { value: "practical_guide", label: "Practical Guide" },
];

const InstructionalMaterials = () => {
  const navigate = useNavigate();

  const {
    materials,
    loading,
    fetchMaterials,
    fetchMaterialDetail,
    createMaterial,
    updateMaterial,
    deleteMaterial,
    archiveMaterial,
    uploadMaterialFiles,
    deleteMaterialFile,
    submitMaterial,
  } = useInstructionalMaterials();

  const store = ValuesStore();
  const roles = appraisalRoles(
    store.getValue("myRoles"),
    store.getValue("user"),
  );

  const { academicYears, fetchAcademicYears } = useAcademicYears();
  const { terms, fetchTerms } = useTerms();

  const [subjectTeachers, setSubjectTeachers] = useState([]);

  // Filters
  const [filterAcadyrID, setFilterAcadyrID] = useState(null);
  const [filterTermID, setFilterTermID] = useState(null);
  const [filterType, setFilterType] = useState(null);
  const [filterStatus, setFilterStatus] = useState(null);

  // Modal state
  const [modalVisible, setModalVisible] = useState(false);
  const [editingMaterial, setEditingMaterial] = useState(null);
  const [saving, setSaving] = useState(false);
  const [deletingFileId, setDeletingFileId] = useState(null);

  // Drawer state
  const [drawerVisible, setDrawerVisible] = useState(false);
  const [viewMaterialId, setViewMaterialId] = useState(null);
  const [viewMaterial, setViewMaterial] = useState(null);

  const fetchSubjectTeachers = useCallback(async () => {
    const res = await Utilities.fetchWithAuth(
      "GET",
      `${domainSettings.urls.academics.my_subjects}?current=true`,
      null,
      navigate,
    );
    if (res?.ok) {
      setSubjectTeachers(
        Array.isArray(res.data) ? res.data : res.data?.results || [],
      );
    }
  }, [navigate]);

  useEffect(() => {
    fetchAcademicYears();
    fetchTerms();
    fetchSubjectTeachers();
  }, [fetchAcademicYears, fetchTerms, fetchSubjectTeachers]);

  const buildParams = () => {
    const params = {};
    if (filterAcadyrID) params.acadyrID = filterAcadyrID;
    if (filterTermID) params.termID = filterTermID;
    if (filterType) params.type = filterType;
    if (filterStatus) params.status = filterStatus;
    return params;
  };

  useEffect(() => {
    fetchMaterials(buildParams());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterAcadyrID, filterTermID, filterType, filterStatus]);

  const refresh = () => fetchMaterials(buildParams());

  const handleView = (record) => {
    setViewMaterialId(record.id);
    setViewMaterial(record);
    setDrawerVisible(true);
  };

  const handleEdit = async (record) => {
    const detail = await fetchMaterialDetail(record.id);
    if (detail) {
      setEditingMaterial(detail);
      setModalVisible(true);
    }
  };

  const handleDelete = async (record) => {
    const ok = await deleteMaterial(record.id);
    if (ok) refresh();
  };

  const handleArchive = async (record) => {
    const ok = await archiveMaterial(record.id);
    if (ok) refresh();
  };

  const handleSubmit = async (record) => {
    const ok = await submitMaterial(record.id);
    if (ok) refresh();
  };

  const handleFormSubmit = async (values, fileList, id) => {
    setSaving(true);
    let result;
    if (id) {
      result = await updateMaterial(id, values);
    } else {
      result = await createMaterial(values);
    }
    if (result && fileList.length > 0) {
      const rawFiles = fileList.map((f) => f.originFileObj || f);
      await uploadMaterialFiles(result.id, rawFiles);
    }
    setSaving(false);
    if (result) {
      setModalVisible(false);
      setEditingMaterial(null);
      refresh();
    }
  };

  const handleDeleteFileInModal = async (materialId, fileId) => {
    setDeletingFileId(fileId);
    const ok = await deleteMaterialFile(materialId, fileId);
    setDeletingFileId(null);
    if (ok && editingMaterial) {
      setEditingMaterial((prev) => ({
        ...prev,
        files: prev.files.filter((f) => f.id !== fileId),
      }));
    }
  };

  // Stats
  const totalCount = materials.length;
  const draftCount = materials.filter((m) => m.status === "Draft").length;
  const pendingCount = materials.filter((m) =>
    m.status?.startsWith("Pending"),
  ).length;
  const approvedCount = materials.filter((m) =>
    ["Approved", "Unlocked"].includes(m.status),
  ).length;
  const unlockedCount = materials.filter(
    (m) => m.status === "Unlocked",
  ).length;
  const deliveredCount = materials.filter((m) => m.isDelivered).length;

  return (
    <div style={{ padding: 24 }}>
      <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
        <Col xs={24} sm={12} md={6}>
          <Card>
            <Statistic
              title="Total Materials"
              value={totalCount}
              prefix={<FileTextOutlined />}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card>
            <Statistic
              title="Draft"
              value={draftCount}
              prefix={<BookOutlined />}
              valueStyle={{ color: "#8c8c8c" }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card>
            <Statistic
              title="Pending Review"
              value={pendingCount}
              prefix={<ClockCircleOutlined />}
              valueStyle={{ color: "#fa8c16" }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card>
            <Statistic
              title="Approved / Unlocked"
              value={approvedCount}
              prefix={<CheckCircleOutlined />}
              valueStyle={{ color: "#52c41a" }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card>
            <Statistic
              title="Delivered (of unlocked)"
              value={deliveredCount}
              suffix={`/ ${unlockedCount} delivered`}
              prefix={<CheckCircleOutlined />}
              valueStyle={{ color: "#1677ff" }}
            />
          </Card>
        </Col>
      </Row>

      <Card
        title="Instructional Materials"
        extra={
          <Space>
            <Button icon={<SyncOutlined />} onClick={refresh} loading={loading}>
              Refresh
            </Button>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => {
                setEditingMaterial(null);
                setModalVisible(true);
              }}
            >
              New Material
            </Button>
          </Space>
        }
      >
        <Row gutter={12} style={{ marginBottom: 16 }}>
          <Col xs={24} sm={12} md={5}>
            <Select
              placeholder="Academic Year"
              allowClear
              style={{ width: "100%" }}
              value={filterAcadyrID}
              onChange={setFilterAcadyrID}
              showSearch
              optionFilterProp="label"
            >
              {academicYears.map((y) => (
                <Select.Option key={y.id} value={y.id} label={y.acadyr}>
                  {y.acadyr}
                </Select.Option>
              ))}
            </Select>
          </Col>
          <Col xs={24} sm={12} md={5}>
            <Select
              placeholder="Term"
              allowClear
              style={{ width: "100%" }}
              value={filterTermID}
              onChange={setFilterTermID}
              showSearch
              optionFilterProp="label"
            >
              {terms.map((t) => (
                <Select.Option key={t.id} value={t.id} label={t.name}>
                  {t.name}
                </Select.Option>
              ))}
            </Select>
          </Col>
          <Col xs={24} sm={12} md={5}>
            <Select
              placeholder="Material Type"
              allowClear
              style={{ width: "100%" }}
              value={filterType}
              onChange={setFilterType}
              options={MATERIAL_TYPE_OPTIONS}
            />
          </Col>
          <Col xs={24} sm={12} md={5}>
            <Select
              placeholder="Status"
              allowClear
              style={{ width: "100%" }}
              value={filterStatus}
              onChange={setFilterStatus}
            >
              <Select.Option value="Draft">Draft</Select.Option>
              <Select.Option value="Pending HoD Review">
                Pending HoD Review
              </Select.Option>
              <Select.Option value="Pending Section Head Validation">
                Pending Section Head
              </Select.Option>
              <Select.Option value="Pending Deputy Head Approval">
                Pending Deputy Head
              </Select.Option>
              <Select.Option value="Rejected">Rejected</Select.Option>
              <Select.Option value="Approved">Approved</Select.Option>
              <Select.Option value="Unlocked">Unlocked</Select.Option>
              <Select.Option value="Archived">Archived</Select.Option>
            </Select>
          </Col>
        </Row>

        <MaterialsTable
          materials={materials}
          loading={loading}
          onView={handleView}
          onEdit={handleEdit}
          onDelete={handleDelete}
          onArchive={handleArchive}
          onSubmit={handleSubmit}
        />
      </Card>

      <MaterialFormModal
        visible={modalVisible}
        onClose={() => {
          setModalVisible(false);
          setEditingMaterial(null);
        }}
        onSubmit={handleFormSubmit}
        onDeleteFile={handleDeleteFileInModal}
        saving={saving}
        deletingFileId={deletingFileId}
        material={editingMaterial}
        subjectTeachers={subjectTeachers}
      />

      <MaterialDetailDrawer
        visible={drawerVisible}
        onClose={() => {
          setDrawerVisible(false);
          setViewMaterialId(null);
          setViewMaterial(null);
        }}
        materialId={viewMaterialId}
        material={viewMaterial}
        onFetchDetail={fetchMaterialDetail}
        onDeleteFile={deleteMaterialFile}
        onUploadFiles={uploadMaterialFiles}
      />
    </div>
  );
};

export default InstructionalMaterials;
